import React, { useEffect } from 'react';

import useUserGroups from '../../../utils/react/hooks/useUserGroups';
import { NoPermissionMessage } from '../../PageLayout';
import { getPageTitle, hasOverlap } from '../../PageLayout/utils';
import { Loading } from '../index.styles';

interface SettingsSectionProps {
  children: React.ReactNode;
  /** Names the browser tab while the section is open. */
  title: string;
  /** Groups the section is for; omitted where every caller may open it. */
  authorisedGroups?: string[];
}

/**
 * One settings page inside the panel: its tab title, and the groups it is for.
 * The page behind the panel carries the layout.
 */
const SettingsSection = ({ children, title, authorisedGroups }: SettingsSectionProps) => {
  const userGroups = useUserGroups();

  useEffect(() => {
    const previousTitle = document.title;
    document.title = getPageTitle(title);

    // The page behind the panel sets its title on mount and not again
    return () => {
      document.title = previousTitle;
    };
  }, [title]);

  // A gated section waits on the groups rather than opening to a reader they
  // may yet refuse; a list read and found empty is a refusal like any other
  if (authorisedGroups !== undefined && userGroups === undefined) {
    return <Loading />;
  }

  const isBarred =
    authorisedGroups !== undefined && !hasOverlap(userGroups ?? [], authorisedGroups);

  return isBarred ? <NoPermissionMessage /> : <>{children}</>;
};

export default SettingsSection;
